import { useState, useMemo } from "react";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Pagination,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from "@mantine/core";
import { useDisclosure, useDebouncedValue } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconEdit,
  IconTrash,
  IconFilter,
  IconRefresh,
} from "@tabler/icons-react";
import {
  useInsuranceProviderPlanTypesAdmin,
  useCreateInsuranceProviderPlanType,
  useUpdateInsuranceProviderPlanType,
  useDeleteInsuranceProviderPlanType,
} from "../../../hooks/admin/useInsuranceProviderPlanTypes";
import { useInsuranceProvidersAdmin } from "../../../hooks/admin/useInsuranceProvidersAdmin";
import { useInsurancePlanTypesAdmin } from "../../../hooks/admin/useInsurancePlanTypes";
import InsuranceProviderPlanTypeForm, {
  InsuranceProviderPlanTypeFormValues,
} from "../../../components/admin/insurance-provider-plan-types/InsuranceProviderPlanTypeForm";
import { InsuranceProviderPlanType } from "../../../services/admin/insurance-provider-plan-types.service";

interface ProviderPlanTypeFilters {
  providerIds?: string[];
  planTypeIds?: string[];
  billingScenario?: string[];
}

const ITEMS_PER_PAGE = 20;

export default function InsuranceProviderPlanTypesAdminPage() {
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    deleteModalOpened,
    { open: openDeleteModal, close: closeDeleteModal },
  ] = useDisclosure(false);

  const [editing, setEditing] = useState<InsuranceProviderPlanType | null>(
    null
  );
  const [itemToDelete, setItemToDelete] =
    useState<InsuranceProviderPlanType | null>(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [filters, setFilters] = useState<ProviderPlanTypeFilters>({});
  const [pendingFilters, setPendingFilters] = useState<ProviderPlanTypeFilters>(
    {}
  );
  const [page, setPage] = useState(1);

  const {
    data: items = [],
    isLoading,
    refetch,
  } = useInsuranceProviderPlanTypesAdmin();
  const { data: providers = [] } = useInsuranceProvidersAdmin();
  const { data: planTypes = [] } = useInsurancePlanTypesAdmin();
  const createItem = useCreateInsuranceProviderPlanType();
  const updateItem = useUpdateInsuranceProviderPlanType();
  const deleteItem = useDeleteInsuranceProviderPlanType();

  const providerOptions = providers
    .filter((p) => p.isActive !== false)
    .map((p) => ({ value: p.id, label: p.name }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const planTypeOptions = planTypes
    .filter((pt) => pt.isActive !== false)
    .map((pt) => ({ value: pt.id, label: pt.type }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const filteredData = useMemo(() => {
    let data = items;

    if (debouncedSearch) {
      const lower = debouncedSearch.toLowerCase();
      data = data.filter(
        (item) =>
          (item.insuranceProvider?.name || "").toLowerCase().includes(lower) ||
          (item.insurancePlanType?.type || "").toLowerCase().includes(lower)
      );
    }

    if (filters.providerIds?.length) {
      data = data.filter((item) =>
        filters.providerIds!.includes(item.insuranceProviderId)
      );
    }

    if (filters.planTypeIds?.length) {
      data = data.filter((item) =>
        filters.planTypeIds!.includes(item.insurancePlanTypeId)
      );
    }

    if (filters.billingScenario?.length) {
      data = data.filter((item) => {
        const scenario = item.allowSplitBilling ? "tc_only" : "global";
        return filters.billingScenario!.includes(scenario);
      });
    }

    return data;
  }, [items, debouncedSearch, filters]);

  const totalPages = Math.ceil(filteredData.length / ITEMS_PER_PAGE);
  const paginatedData = useMemo(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    return filteredData.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredData, page]);

  const hasActiveFilters =
    !!filters.providerIds?.length ||
    !!filters.planTypeIds?.length ||
    !!filters.billingScenario?.length;

  const handleAdd = () => {
    setEditing(null);
    openFormModal();
  };

  const handleEdit = (item: InsuranceProviderPlanType) => {
    setEditing(item);
    openFormModal();
  };

  const handleSubmit = async (values: InsuranceProviderPlanTypeFormValues) => {
    if (editing) {
      await updateItem.mutateAsync({
        id: editing.id,
        allowSplitBilling: values.allowSplitBilling,
        tebraCompanyId:
          values.tebraCompanyId === "" ? null : values.tebraCompanyId,
        tebraPlanId: values.tebraPlanId === "" ? null : values.tebraPlanId,
      });
    } else {
      await createItem.mutateAsync({
        ...values,
        tebraCompanyId:
          values.tebraCompanyId === "" ? null : values.tebraCompanyId,
        tebraPlanId: values.tebraPlanId === "" ? null : values.tebraPlanId,
      });
    }
    closeFormModal();
    setEditing(null);
  };

  const handleDelete = (item: InsuranceProviderPlanType) => {
    setItemToDelete(item);
    openDeleteModal();
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      await deleteItem.mutateAsync(itemToDelete.id);
      closeDeleteModal();
      setItemToDelete(null);
    }
  };

  const handleClearAllFilters = () => {
    setFilters({});
    setPendingFilters({});
    setPage(1);
  };

  const handleOpenFilterModal = () => {
    setPendingFilters({ ...filters });
    openFilterModal();
  };

  const handleApplyFilters = () => {
    setFilters({ ...pendingFilters });
    setPage(1);
    closeFilterModal();
  };

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={2}>Insurance Provider Plan Types</Title>
              <Text size="sm" c="dimmed" mt="xs">
                Configure billing rules for each insurance provider and plan
                type combination
              </Text>
            </div>
            <Button leftSection={<IconPlus size={16} />} onClick={handleAdd}>
              Add New
            </Button>
          </Group>

          {/* Search and Filters Row */}
          <Group align="stretch" grow>
            <Paper p="md" withBorder style={{ flex: 2 }}>
              <Stack gap="sm">
                <Group>
                  <IconSearch size={20} />
                  <Text fw={500}>Search</Text>
                </Group>
                <TextInput
                  placeholder="Search by provider or plan type..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  leftSection={<IconSearch size={16} />}
                />
              </Stack>
            </Paper>

            <Paper p="md" withBorder style={{ flex: 1 }}>
              <Stack gap="sm">
                <Group justify="space-between">
                  <Group>
                    <IconFilter size={20} />
                    <Text fw={500}>Filters</Text>
                  </Group>
                  <Button
                    variant="subtle"
                    size="compact-sm"
                    onClick={() => refetch()}
                    loading={isLoading}
                    leftSection={<IconRefresh size={16} />}
                  />
                </Group>
                <Button
                  variant="light"
                  onClick={handleOpenFilterModal}
                  fullWidth
                >
                  {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                </Button>
                {hasActiveFilters && (
                  <Group gap="xs" wrap="wrap">
                    <Text size="sm" c="dimmed">
                      Active:
                    </Text>
                    {(filters.providerIds?.length ?? 0) > 0 && (
                      <Badge variant="light" color="blue">
                        {filters.providerIds!.length} Provider
                        {filters.providerIds!.length !== 1 ? "s" : ""}
                      </Badge>
                    )}
                    {(filters.planTypeIds?.length ?? 0) > 0 && (
                      <Badge variant="light" color="grape">
                        {filters.planTypeIds!.length} Plan Type
                        {filters.planTypeIds!.length !== 1 ? "s" : ""}
                      </Badge>
                    )}
                    {(filters.billingScenario?.length ?? 0) > 0 && (
                      <Badge variant="light" color="teal">
                        {filters.billingScenario!.length} Billing
                      </Badge>
                    )}
                    <Button
                      variant="subtle"
                      size="compact-xs"
                      color="gray"
                      onClick={handleClearAllFilters}
                    >
                      Clear All
                    </Button>
                  </Group>
                )}
                <Text size="sm" c="dimmed">
                  {filteredData.length} rule
                  {filteredData.length !== 1 ? "s" : ""} found
                </Text>
              </Stack>
            </Paper>
          </Group>

          {/* Table Section */}
          <Paper p="md" withBorder>
            <Stack gap="md">
              <Group justify="space-between">
                <Text fw={500} size="lg">
                  Provider Plan Types ({filteredData.length})
                </Text>
                {totalPages > 1 && (
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      Showing {(page - 1) * ITEMS_PER_PAGE + 1}–
                      {Math.min(page * ITEMS_PER_PAGE, filteredData.length)} of{" "}
                      {filteredData.length}
                    </Text>
                    <Pagination
                      total={totalPages}
                      value={page}
                      onChange={setPage}
                      size="sm"
                    />
                  </Group>
                )}
              </Group>

              {isLoading ? (
                <Center p="xl">
                  <Loader />
                </Center>
              ) : filteredData.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {hasActiveFilters || search
                    ? "No billing rules match the current filters"
                    : "No billing rules found"}
                </Text>
              ) : (
                <Table striped highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>Insurance Provider</Table.Th>
                      <Table.Th>Plan Type</Table.Th>
                      <Table.Th style={{ width: 180 }}>
                        Billing Scenario
                      </Table.Th>
                      <Table.Th style={{ width: 190 }}>
                        Tebra Payer Mapping
                      </Table.Th>
                      <Table.Th style={{ width: 100 }}>Actions</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {paginatedData.map((item) => (
                      <Table.Tr key={item.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {item.insuranceProvider?.name || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">
                            {item.insurancePlanType?.type || "—"}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            color={item.allowSplitBilling ? "blue" : "gray"}
                            variant="light"
                          >
                            {item.allowSplitBilling ? "TC Only" : "Global"}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          {item.tebraCompanyId !== null &&
                          item.tebraPlanId !== null ? (
                            <Text size="sm">
                              Company {item.tebraCompanyId} / Plan{" "}
                              {item.tebraPlanId}
                            </Text>
                          ) : (
                            <Badge color="orange" variant="light">
                              Not configured
                            </Badge>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Tooltip label="Edit">
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleEdit(item)}
                              >
                                <IconEdit size={18} />
                              </ActionIcon>
                            </Tooltip>
                            <Tooltip label="Delete">
                              <ActionIcon
                                variant="subtle"
                                color="red"
                                onClick={() => handleDelete(item)}
                              >
                                <IconTrash size={18} />
                              </ActionIcon>
                            </Tooltip>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Stack>
          </Paper>
        </Stack>

        {/* Create/Edit Modal */}
        <Modal
          opened={formModalOpened}
          onClose={closeFormModal}
          title={editing ? "Edit Billing Rule" : "Add New Billing Rule"}
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <InsuranceProviderPlanTypeForm
              initialValues={editing || undefined}
              onSubmit={handleSubmit}
            />
            <Group justify="flex-end" mt="md">
              <Button variant="default" onClick={closeFormModal}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={createItem.isPending || updateItem.isPending}
                onClick={() => {
                  const form = document.querySelector("form");
                  if (form) form.requestSubmit();
                }}
              >
                {editing ? "Update" : "Create"}
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Billing Rules"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Insurance Providers"
              placeholder="All providers"
              data={providerOptions}
              value={pendingFilters.providerIds || []}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  providerIds: value.length ? value : undefined,
                })
              }
              searchable
              clearable
            />
            <MultiSelect
              label="Plan Types"
              placeholder="All plan types"
              data={planTypeOptions}
              value={pendingFilters.planTypeIds || []}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  planTypeIds: value.length ? value : undefined,
                })
              }
              searchable
              clearable
            />
            <MultiSelect
              label="Billing Scenario"
              placeholder="All scenarios"
              data={[
                { value: "global", label: "Global" },
                { value: "tc_only", label: "TC Only" },
              ]}
              value={pendingFilters.billingScenario || []}
              onChange={(value) =>
                setPendingFilters({
                  ...pendingFilters,
                  billingScenario: value.length ? value : undefined,
                })
              }
              clearable
            />
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={() => setPendingFilters({})}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpened}
          onClose={closeDeleteModal}
          title="Delete Billing Rule"
          size="sm"
          centered
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to delete the billing rule for{" "}
              <strong>
                {itemToDelete?.insuranceProvider?.name || "Unknown"} –{" "}
                {itemToDelete?.insurancePlanType?.type || "Unknown"}
              </strong>
              ?
            </Text>
            <Text size="sm" c="dimmed">
              This action cannot be undone. Any physician overrides for this
              rule must be removed first.
            </Text>
            <Group justify="flex-end" mt="md" gap="xs">
              <Button variant="default" onClick={closeDeleteModal}>
                Cancel
              </Button>
              <Button
                color="red"
                onClick={confirmDelete}
                loading={deleteItem.isPending}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Box>
    </Container>
  );
}
